import { useTalkReadMessage } from "@mgdx/talk/hooks/useTalkReadMessage";
import { useTalkRoomAndMessageCollection } from "@mgdx/talk/hooks/useTalkRoomAndMessageCollection";
import { useTalkSendImage } from "@mgdx/talk/hooks/useTalkSendImage";
import { useTalkSendMessage } from "@mgdx/talk/hooks/useTalkSendMessage";
import { SenderTypeEnum } from "@mgdx/talk/models/TalkMessageDocument";
import { useTalkStore } from "@mgdx/talk/providers/TalkStoreProvider";
import { OnVisibleUnreadMessage } from "@mgdx/talk/ui/TalkBox";
import { TalkFormValues } from "@mgdx/talk/ui/TalkForm";
import { TalkMessageListView } from "@mgdx/talk/views/TalkMessageListView";
import { Button } from "@mgdx/ui/components/Button";
import { navigate } from "@mgdx-libs/link";
import { useCallback, useMemo } from "react";

export type PageProps = { roomId: string };

export type Props = {
  roomId: string;
  showCounselingLink?: boolean;
};

export const TalkMessageListContent = ({ roomId, showCounselingLink = true }: Props) => {
  const { clearSelectedTalkRoomId, closeTalk } = useTalkStore((state) => state.actions);
  const { room, messages, isLoading } = useTalkRoomAndMessageCollection({ roomId });
  const readMessage = useTalkReadMessage({ roomId, talkSenderType: SenderTypeEnum.Patient });
  const sendMessage = useTalkSendMessage({ roomId, talkSenderType: SenderTypeEnum.Patient });
  const sendImage = useTalkSendImage({ roomId, room, talkSenderType: SenderTypeEnum.Patient });

  const handleSubmit = useCallback(
    async (values: TalkFormValues) => {
      if (values.file) {
        await sendImage(values.file);
      } else if (values.message) {
        await sendMessage({
          content: values.message,
        });
      }
    },
    [sendImage, sendMessage]
  );

  const handleVisibleUnreadMessage = useCallback<OnVisibleUnreadMessage>(
    async (messageId) => {
      await readMessage(messageId);
    },
    [readMessage]
  );

  const counselingDetailPath = useMemo(() => {
    if (!showCounselingLink || !room) return undefined;
    const { counselingId, pharmacy, clinic } = room;
    if (!counselingId) return undefined;

    if (pharmacy) {
      return `/pharmacy-counselings/${counselingId}`;
    }
    if (clinic) {
      return `/clinic-counselings/${counselingId}`;
    }
    return undefined;
  }, [showCounselingLink, room]);

  const handleClickCounselingDetail = useCallback(() => {
    if (!counselingDetailPath) return;
    clearSelectedTalkRoomId();
    closeTalk();
    navigate(counselingDetailPath);
  }, [clearSelectedTalkRoomId, closeTalk, counselingDetailPath]);

  const bottomContent = counselingDetailPath ? (
    <Button variant="outlined" className="mt-2 mx-auto" onClick={handleClickCounselingDetail}>
      予約詳細を見る
    </Button>
  ) : undefined;

  return (
    <TalkMessageListView
      talkSenderType={SenderTypeEnum.Patient}
      roomId={roomId}
      room={room}
      messages={messages}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onVisibleUnreadMessage={handleVisibleUnreadMessage}
      bottomContent={bottomContent}
    />
  );
};
